import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Wallet as WalletIcon,
  Send,
  Phone,
  User,
  Loader2,
  CheckCircle2,
  XCircle,
  ArrowDown,
  Filter,
  Smartphone,
  ShieldCheck,
  Plus,
  X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import SendCashReceipt from "@/components/SendCashReceipt";
import WalletDetailModal from "@/components/WalletDetailModal";
import DateRangeButton from "@/components/DateRangeButton";
import Pagination from "@/components/Pagination";
import { formatUserLabel } from "@/lib/formatUser";

const inputCls =
  "flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

const PROVIDER_THEME = {
  moncash: { label: "MonCash", bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  natcash: { label: "NatCash", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
};

const CURRENCY_TONES = {
  USD: "bg-[#2BA8F0]",
  CAD: "bg-red-500",
  CLP: "bg-amber-500",
  DOP: "bg-blue-700",
  HTG: "bg-violet-600",
  EUR: "bg-indigo-500",
  BRL: "bg-emerald-600",
  MXN: "bg-teal-600",
};

const NO_DECIMAL = new Set(["CLP", "HTG"]);

function fmtCash(amount, currency) {
  const noDec = NO_DECIMAL.has(currency);
  const n = Number(amount || 0);
  if (noDec) return `${Math.round(n).toLocaleString("en-US")} ${currency}`;
  return `${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function StatusPill({ status }) {
  const map = {
    SUCCESS: "bg-emerald-50 text-emerald-700 border-emerald-200",
    FAILED: "bg-rose-50 text-rose-700 border-rose-200",
    PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${map[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {status}
    </span>
  );
}

function FundModal({ currency, onClose, onDone }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0) { toast.error(t("ui.enter_valid_amount")); return; }
    setBusy(true);
    try {
      await api.post("/cash-wallets/credit", { currency, amount: n, note: note.trim() || null });
      toast.success(`Credited ${fmtCash(n, currency)} to your ${currency} wallet`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("cash.failed_credit"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-md p-6 animate-in zoom-in-95 duration-200">
        <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Fund {currency} Send Cash wallet
        </div>
        <div className="text-xs text-slate-500 mb-4">Manual top-up — admins only. Use this when funds arrive via wire / cash deposit.</div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Amount ({currency})</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputCls}
              placeholder="0.00"
              autoFocus
              data-testid="fund-amount-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Note (optional)</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputCls}
              placeholder="e.g. Wire from CitiBank — Apr 12"
              maxLength={300}
              data-testid="fund-note-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
            <button
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="fund-submit"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Credit wallet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VerifyModal({ tx, onClose, onDone }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("SUCCESS");
  const [verifierProvider, setVerifierProvider] = useState(tx.wallet_provider || "");
  const [verifierUsername, setVerifierUsername] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (status === "FAILED" && !reason.trim()) {
      toast.error(t("cash.provide_reject_reason"));
      return;
    }
    if (status === "SUCCESS" && !verifierUsername.trim()) {
      toast.error(t("cash.provide_verifier_account"));
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/send-cash/${tx.id}/status`, {
        status,
        verifier_provider: verifierProvider || tx.wallet_provider,
        verifier_username: verifierUsername.trim() || null,
        reason: reason.trim() || null,
      });
      toast.success(`Marked ${tx.reference} as ${status}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-md p-6 animate-in zoom-in-95 duration-200">
        <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Verify {tx.reference}
        </div>
        <div className="text-xs text-slate-500 mb-4">
          {tx.recipient_name} · {tx.recipient_phone} · {fmtCash(tx.amount_sent || tx.amount, tx.currency)}
          {tx.amount_received ? ` → ${fmtCash(tx.amount_received, tx.receive_currency || "HTG")}` : ""}
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Decision</label>
            <div className="flex gap-2">
              {[{ v: "SUCCESS", color: "emerald" }, { v: "FAILED", color: "rose" }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`verify-decision-${o.v}`}
                  onClick={() => setStatus(o.v)}
                  className={`flex-1 h-10 rounded-md border text-sm font-semibold ${
                    status === o.v
                      ? `bg-${o.color}-600 text-white border-${o.color}-600`
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {o.v === "SUCCESS" ? "Confirm" : "Reject"}
                </button>
              ))}
            </div>
          </div>
          {status === "SUCCESS" && (
            <>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Verifier provider</label>
                <div className="flex gap-2">
                  {["moncash", "natcash"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`verifier-provider-${p}`}
                      onClick={() => setVerifierProvider(p)}
                      className={`flex-1 h-10 rounded-md border text-sm font-semibold capitalize ${
                        verifierProvider === p
                          ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {p === "moncash" ? "MonCash" : "NatCash"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                  Verifier account username
                </label>
                <input
                  data-testid="verifier-username"
                  value={verifierUsername}
                  onChange={(e) => setVerifierUsername(e.target.value)}
                  className={inputCls}
                  placeholder="The MonCash/NatCash account that confirmed the transfer"
                />
              </div>
            </>
          )}
          {status === "FAILED" && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                Rejection reason <span className="text-rose-500">*</span>
              </label>
              <textarea
                data-testid="reject-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="flex w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                placeholder="e.g. Wallet not found, balance returned to agent."
                maxLength={300}
              />
              <div className="text-[11px] text-rose-700 mt-1">A FAILED status auto-reverses the debit back to the agent.</div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="verify-submit"
              className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-50 ${
                status === "SUCCESS" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
              }`}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {status === "SUCCESS" ? "Confirm transaction" : "Reject (auto-reverse)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SendCash() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { has: hasPerm, isCollab } = usePermissions();
  const navigate = useNavigate();
  const isAdmin = user?.role === "MASTER_ADMIN";
  // Managers can see the multi-currency wallet grid. A COLLABORATOR is a
  // delegated admin — they get the grid when they have
  // `view_all_send_cash_wallets`. Backend enforces the same rule.
  const isManager =
    isAdmin ||
    user?.role === "MASTER_AGENT" ||
    (isCollab && hasPerm("view_all_send_cash_wallets"));
  const canTransferCash = isAdmin || user?.role === "MASTER_AGENT" || (isCollab && hasPerm("transfer_cash"));
  const canViewCashVerification = isAdmin || (isCollab && hasPerm("view_cash_verification"));

  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);

  // Wallet-detail modal (opens when a currency card is clicked)
  const [walletDetail, setWalletDetail] = useState(null); // { currency, balance } | null

  // Fee tiers (admin-configured per currency)
  const [feeTiersByCurrency, setFeeTiersByCurrency] = useState({});

  // Form state
  const [fullName, setFullName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [baseAmount, setBaseAmount] = useState("");
  const [convenienceFee, setConvenienceFee] = useState("");
  const [note, setNote] = useState("");
  const [detection, setDetection] = useState(null);
  const [manualProvider, setManualProvider] = useState("");
  const [quote, setQuote] = useState(null);   // live quote returned by /api/cash/quote
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastTx, setLastTx] = useState(null);

  // Recent transactions
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilter, setHistoryFilter] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null); // { from, to, label } | null
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);

  // Modals
  const [fundingCurrency, setFundingCurrency] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [receiptTx, setReceiptTx] = useState(null);

  const detectTimer = useRef(null);

  const loadWallet = async () => {
    setWalletLoading(true);
    try {
      const { data } = await api.get("/cash-wallet/me");
      setWallet(data);
    } finally { setWalletLoading(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const skip = (historyPage - 1) * historyPageSize;
      const params = new URLSearchParams({ limit: String(historyPageSize), skip: String(skip) });
      if (historyFilter) params.set("status", historyFilter);
      if (dateRange?.from) params.set("date_from", dateRange.from);
      if (dateRange?.to) params.set("date_to", dateRange.to);
      const { data } = await api.get(`/send-cash?${params.toString()}`);
      setHistory(data.items || []);
      setHistoryTotal(data.total || 0);
    } finally { setHistoryLoading(false); }
  };

  useEffect(() => { loadWallet(); }, []);
  useEffect(() => { loadHistory(); }, [historyFilter, dateRange, historyPage, historyPageSize]);

  // Load fee tiers once — used to display the max-fee chip even before quote.
  useEffect(() => {
    api.get("/cash/fee-tiers").then((r) => {
      setFeeTiersByCurrency(r.data?.tiers_by_currency || {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (detectTimer.current) clearTimeout(detectTimer.current);
    setManualProvider("");
    if (!recipientPhone || recipientPhone.replace(/\D/g, "").length < 4) {
      setDetection(null);
      return;
    }
    detectTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/catalog/detect-cash-provider?phone=${encodeURIComponent(recipientPhone)}`);
        setDetection(data);
      } catch {
        setDetection(null);
      }
    }, 350);
  }, [recipientPhone]);

  // Live quote: re-fetch whenever phone / amount / fee changes.
  const quoteTimer = useRef(null);
  useEffect(() => {
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    const base = Number(baseAmount);
    if (!recipientPhone || recipientPhone.replace(/\D/g, "").length < 4 || !base || base <= 0) {
      setQuote(null);
      return;
    }
    setQuoting(true);
    quoteTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/cash/quote", {
          recipient_phone: recipientPhone,
          base_amount: base,
          convenience_fee: Number(convenienceFee) || 0,
        });
        setQuote(data);
      } catch {
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 250);
  }, [recipientPhone, baseAmount, convenienceFee]);

  const effectiveProvider = useMemo(() => {
    if (manualProvider) return manualProvider;
    return quote?.wallet_provider || detection?.wallet_provider || "";
  }, [manualProvider, quote, detection]);

  // Max fee for the current amount + currency (client-side guard, server is source of truth)
  const currentMaxFee = useMemo(() => {
    if (quote?.max_fee !== undefined) return Number(quote.max_fee);
    const ccy = wallet?.currency || "USD";
    const tiers = feeTiersByCurrency[ccy] || [];
    const amt = Number(baseAmount) || 0;
    for (const t of tiers) {
      const hi = t.max_amount == null ? Infinity : Number(t.max_amount);
      if (amt >= Number(t.min_amount) && amt <= hi) return Number(t.max_fee);
    }
    return 0;
  }, [quote, feeTiersByCurrency, wallet, baseAmount]);

  const feeError = useMemo(() => {
    const fee = Number(convenienceFee) || 0;
    if (fee > currentMaxFee + 1e-6) {
      return `Max convenience fee is ${currentMaxFee} for this amount range.`;
    }
    return "";
  }, [convenienceFee, currentMaxFee]);

  const canSend = useMemo(() => {
    if (fullName.trim().length < 2) return false;
    if (!recipientPhone.trim()) return false;
    const base = Number(baseAmount);
    if (!base || base <= 0) return false;
    if (feeError) return false;
    if (!effectiveProvider) return false;
    if (!wallet || wallet.balance < base) return false;
    if (!quote || quote.exchange_rate <= 0) return false;
    return true;
  }, [fullName, recipientPhone, baseAmount, feeError, effectiveProvider, wallet, quote]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/send-cash", {
        recipient_name: fullName.trim(),
        recipient_phone: recipientPhone.trim(),
        base_amount: Number(baseAmount),
        convenience_fee: Number(convenienceFee) || 0,
        note: note.trim() || null,
      });
      toast.success(`Send Cash created · ${data.reference}`);
      setLastTx(data);
      setReceiptTx(data); // Show the customer receipt immediately
      setFullName("");
      setRecipientPhone("");
      setBaseAmount("");
      setConvenienceFee("");
      setNote("");
      setDetection(null);
      setQuote(null);
      setManualProvider("");
      loadWallet();
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("cash.failed_send"));
    } finally { setBusy(false); }
  };

  const currentCurrency = wallet?.currency || "USD";

  return (
    <div className="space-y-6" data-testid="send-cash-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Smartphone className="w-3.5 h-3.5 text-emerald-600" /> {t("cash.title")}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("cash.title")}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {t("cash.subtitle")}
          </p>
        </div>
        <Link
          to="/send-topup"
          className="inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-semibold"
        >
          <Send className="w-3.5 h-3.5" /> {t("send_cash.switch_to_topup")}
        </Link>
      </div>

      {/* Wallet grid + Transfer Cash CTA underneath */}
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            {isManager ? t("send_cash.wallets_all") : "Your Cash Wallet"}
          </div>
          <DateRangeButton value={dateRange} onChange={setDateRange} testId="send-cash-date-range" />
        </div>
        {isManager ? (
          // Admin / master: grid of all 7 currency wallets
          <div data-testid="cash-wallet-grid">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(wallet?.balances || []).map((b) => (
                <button
                  type="button"
                  key={b.currency}
                  data-testid={`cash-balance-${b.currency}`}
                  onClick={() => setWalletDetail({ currency: b.currency, balance: b.balance })}
                  className={`${CURRENCY_TONES[b.currency] || "bg-slate-500"} text-white rounded-md p-4 relative text-left transition-transform hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/60 cursor-pointer`}
                >
                  <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold">{b.currency}</div>
                  <div
                    className="text-2xl font-bold mt-1 leading-tight break-words"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {fmtCash(b.balance, b.currency).replace(` ${b.currency}`, "")}
                  </div>
                  {isAdmin && (
                    <span
                      role="button"
                      data-testid={`fund-btn-${b.currency}`}
                      onClick={(e) => { e.stopPropagation(); setFundingCurrency(b.currency); }}
                      className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 text-white cursor-pointer"
                      title={`Fund ${b.currency}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : (
          // Agent: single big wallet card
          <button
            type="button"
            data-testid="cash-wallet-card"
            onClick={() => setWalletDetail({ currency: currentCurrency, balance: wallet?.balance ?? 0 })}
            className={`${CURRENCY_TONES[currentCurrency]} text-white rounded-md p-5 w-full text-left transition-transform hover:scale-[1.01] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/60 cursor-pointer`}
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90">
              <WalletIcon className="w-4 h-4" /> {t("send_cash.wallet_balance_label")}
            </div>
            <div
              className="text-4xl font-bold mt-2"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="cash-wallet-balance"
            >
              {walletLoading ? "—" : fmtCash(wallet?.balance ?? 0, currentCurrency)}
            </div>
            <div className="text-xs opacity-90 mt-1">{t("ui.currency")} · {currentCurrency}</div>
            <div className="text-[11px] mt-3 opacity-80 leading-relaxed max-w-md">
              {t("send_cash.wallet_hint")}
            </div>
          </button>
        )}

        {/* Transfer Cash CTA — placed clearly BELOW the wallet cards */}
        {(canTransferCash || canViewCashVerification) && (
          <div className="flex flex-wrap items-center gap-2" data-testid="transfer-cash-cta-row">
            {canTransferCash && (
              <button
                onClick={() => navigate("/agents")}
                data-testid="open-transfer-cash"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
              >
                <ArrowDown className="w-4 h-4" />
                {t("send_cash.transfer_cash_agent")}
              </button>
            )}
            {canViewCashVerification && (
              <Link
                to="/cash-verification"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                data-testid="open-cash-verification"
              >
                <ShieldCheck className="w-4 h-4" />
                {t("send_cash.open_cash_verification")}
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Form */}
      <form onSubmit={submit} className="bg-white rounded-md border border-slate-200 p-5 space-y-4" data-testid="send-cash-form">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            {t("send_cash.full_name_required")}
          </label>
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="send-cash-full-name"
              id="send-cash-full-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={`${inputCls} pl-9`}
              placeholder="Marie Joseph"
              required
              autoComplete="off"
              aria-label="Recipient full name"
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            {t("send_cash.full_name_hint")}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            {t("send_cash.recipient_phone_required")}
            <span
              data-testid="cash-prefix-chip"
              className="ml-1 text-[10px] font-bold tracking-wider bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full border border-violet-200"
            >
              {t("send_cash.haiti_prefix")}
            </span>
          </label>
          <div className="flex items-stretch">
            <div
              data-testid="cash-prefix-badge"
              className="inline-flex items-center justify-center px-3 rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-sm font-bold font-mono text-slate-700 select-none min-w-[64px]"
              title={t("send_cash.haiti_country_code")}
            >
              +509
            </div>
            <input
              data-testid="send-cash-phone"
              id="send-cash-phone"
              name="send-cash-phone"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
              className={`${inputCls} rounded-l-none font-mono flex-1`}
              placeholder="37 31 16 28"
              inputMode="tel"
              required
              aria-label={t("send_cash.recipient_phone_required")}
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            {t("send_cash.phone_hint")}
          </div>
          {recipientPhone && (
            <div className="mt-2 text-xs" data-testid="cash-detection">
              {detection?.supported ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${PROVIDER_THEME[detection.wallet_provider]?.bg} ${PROVIDER_THEME[detection.wallet_provider]?.text} ${PROVIDER_THEME[detection.wallet_provider]?.border} text-[11px] font-semibold`}>
                    <ShieldCheck className="w-3 h-3" />
                    {t("send_cash.detected")}: {detection.operator_name} · {detection.wallet_provider_label}
                  </span>
                  {isAdmin && (
                    <span className="text-[11px] text-slate-500">
                      {t("send_cash.admin_override")}
                      {["moncash", "natcash"].map((p) => (
                        <button
                          type="button"
                          key={p}
                          data-testid={`override-${p}`}
                          onClick={() => setManualProvider(manualProvider === p ? "" : p)}
                          className={`ml-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${
                            manualProvider === p
                              ? "bg-violet-600 text-white border-violet-600"
                              : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-amber-700 font-medium">
                  {t("send_cash.could_not_detect")}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("send_cash.amount_to_send_ccy", { ccy: currentCurrency })}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-semibold">{currentCurrency}</span>
              <input
                data-testid="send-cash-amount"
                id="send-cash-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={baseAmount}
                onChange={(e) => setBaseAmount(e.target.value)}
                className={`${inputCls} pl-14 font-semibold`}
                placeholder="0.00"
                required
                aria-label={t("send_cash.amount_to_send")}
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{t("send_cash.only_amount_debited")}</div>
            {wallet && Number(baseAmount) > Number(wallet.balance) && (
              <div className="text-[11px] text-rose-600 mt-1" data-testid="send-cash-insufficient">
                {t("send_cash.exceeds_balance", { balance: fmtCash(wallet.balance, currentCurrency) })}
              </div>
            )}
          </div>
          <div>
            <label className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              <span>{t("send_cash.convenience_fee_ccy", { ccy: currentCurrency })}</span>
              <span
                data-testid="send-cash-max-fee"
                className="text-[10px] font-bold tracking-wider bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200"
              >
                {t("send_cash.max_fee_ccy", { n: currentMaxFee, ccy: currentCurrency })}
              </span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-semibold">{currentCurrency}</span>
              <input
                data-testid="send-cash-fee"
                type="number"
                step="0.01"
                min="0"
                max={currentMaxFee || undefined}
                value={convenienceFee}
                onChange={(e) => {
                  // Clamp client-side so the agent can never even type above the max
                  const v = e.target.value;
                  if (v === "" || Number(v) <= currentMaxFee + 1e-6) {
                    setConvenienceFee(v);
                  } else {
                    setConvenienceFee(String(currentMaxFee));
                  }
                }}
                className={`${inputCls} pl-14 font-semibold ${feeError ? "border-rose-400" : ""}`}
                placeholder="0.00"
                disabled={!baseAmount || currentMaxFee <= 0}
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {t("send_cash.profit_kept")}
            </div>
            {feeError && (
              <div className="text-[11px] text-rose-600 mt-1" data-testid="send-cash-fee-error">{feeError}</div>
            )}
          </div>
        </div>

        {/* Live summary panel — Total to pay + Receiver gets */}
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="send-cash-summary">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{t("send_cash.live_summary")}</span>
            {quoting && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("send_cash.amount_to_send")}</div>
              <div className="text-lg font-bold text-slate-900 mt-0.5" data-testid="summary-amount">
                {fmtCash(Number(baseAmount) || 0, currentCurrency)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("send_cash.convenience_fee")}</div>
              <div className="text-lg font-bold text-amber-700 mt-0.5" data-testid="summary-fee">
                {fmtCash(Number(convenienceFee) || 0, currentCurrency)}
              </div>
            </div>
            <div className="border-l border-slate-200 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("send_cash.total_to_pay")}</div>
              <div className="text-lg font-bold text-[#0F62FE] mt-0.5" data-testid="summary-total">
                {fmtCash(quote?.total_paid ?? ((Number(baseAmount) || 0) + (Number(convenienceFee) || 0)), currentCurrency)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("send_cash.receiver_gets")}</div>
              <div className="text-lg font-bold text-emerald-700 mt-0.5" data-testid="summary-receiver">
                {fmtCash(quote?.receiver_gets ?? 0, "HTG")}
              </div>
            </div>
          </div>
          {quote?.exchange_rate > 0 && (
            <div className="text-[11px] text-slate-500 mt-3">
              {t("send_cash.exchange_rate")} · 1 {currentCurrency} = {quote.exchange_rate.toLocaleString("en-US")} HTG
            </div>
          )}
        </div>

        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            {t("send_cash.note_optional_upper")}
          </label>
          <input
            data-testid="send-cash-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputCls}
            placeholder={t("send_cash.internal_reference")}
            maxLength={300}
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-200">
          <button
            type="submit"
            data-testid="send-cash-submit"
            disabled={!canSend || busy}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Cash
          </button>
        </div>

        {lastTx && (
          <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm flex items-center justify-between gap-3" data-testid="last-tx-banner">
            <div className="text-xs text-emerald-700 leading-relaxed">
              <strong>{lastTx.reference}</strong> — request received, customer receipt generated.
            </div>
            <button
              type="button"
              onClick={() => setReceiptTx(lastTx)}
              data-testid="reopen-receipt-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
            >
              Show receipt
            </button>
          </div>
        )}
      </form>

      {/* History */}
      <div className="bg-white rounded-md border border-slate-200" data-testid="send-cash-history-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Recent Send Cash transactions</div>
            <div className="text-sm text-slate-700 mt-0.5">{historyTotal} total</div>
          </div>
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            {["", "PENDING", "SUCCESS", "FAILED"].map((s) => (
              <button
                key={s || "all"}
                data-testid={`filter-${s || "all"}`}
                onClick={() => setHistoryFilter(s)}
                className={`text-[11px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${
                  historyFilter === s
                    ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {s || "All"}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="send-cash-history-table">
            <thead>
              <tr>
                {[
                  { k: "ref", label: "Ref", align: "left" },
                  { k: "created", label: "Created", align: "left" },
                  ...(isManager ? [{ k: "agent", label: "Agent", align: "left" }] : []),
                  { k: "full_name", label: "Full name", align: "left" },
                  { k: "phone", label: "Phone", align: "left" },
                  { k: "provider", label: "Provider", align: "left" },
                  { k: "sent", label: "Sent", align: "right" },
                  { k: "receives", label: "Receives", align: "right" },
                  { k: "status", label: "Status", align: "left" },
                  ...(isManager ? [{ k: "action", label: "Action", align: "left" }] : []),
                ].map((c) => (
                  <th key={c.k} className={`bg-slate-50 ${c.align === "right" ? "text-right" : "text-left"} text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historyLoading && (
                <tr><td colSpan={isManager ? 10 : 8} className="px-5 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
              )}
              {!historyLoading && history.length === 0 && (
                <tr><td colSpan={isManager ? 10 : 8} className="px-5 py-8 text-center text-slate-500 text-sm">No Send Cash transactions yet.</td></tr>
              )}
              {history.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sc-row-${t.reference}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 font-semibold">{t.reference}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}</td>
                  {isManager && (
                    <td className="px-4 py-3 text-xs text-slate-800 font-semibold">
                      {formatUserLabel({
                        name: t.agent_name,
                        username: t.agent_username,
                        agent_code: t.agent_code,
                        role: t.agent_role,
                      })}
                    </td>
                  )}
                  <td className="px-4 py-3 text-slate-900 font-semibold">{t.recipient_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{t.recipient_phone}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${PROVIDER_THEME[t.wallet_provider]?.bg} ${PROVIDER_THEME[t.wallet_provider]?.text} ${PROVIDER_THEME[t.wallet_provider]?.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${PROVIDER_THEME[t.wallet_provider]?.dot}`} />
                      {t.wallet_provider_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap text-right">{fmtCash(t.amount_sent || t.amount, t.currency)}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-700 whitespace-nowrap text-right">
                    {t.amount_received ? fmtCash(t.amount_received, t.receive_currency || "HTG") : "—"}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  {isManager && (
                    <td className="px-4 py-3">
                      {t.status === "PENDING" ? (
                        <button
                          data-testid={`verify-${t.reference}`}
                          onClick={() => setVerifying(t)}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-[#0F62FE] text-white text-[11px] font-semibold hover:bg-[#0050E6]"
                        >
                          <ShieldCheck className="w-3 h-3" /> Verify
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400">
                          {t.status === "FAILED" && t.reason ? `· ${t.reason.slice(0, 30)}` : "—"}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={historyPage}
          pageSize={historyPageSize}
          total={historyTotal}
          onPage={setHistoryPage}
          onPageSize={setHistoryPageSize}
          testId="sendcash-history-pagination"
        />
      </div>

      {fundingCurrency && (
        <FundModal
          currency={fundingCurrency}
          onClose={() => setFundingCurrency(null)}
          onDone={() => { setFundingCurrency(null); loadWallet(); }}
        />
      )}
      {verifying && (
        <VerifyModal
          tx={verifying}
          onClose={() => setVerifying(null)}
          onDone={() => { setVerifying(null); loadHistory(); loadWallet(); }}
        />
      )}
      {receiptTx && (
        <SendCashReceipt tx={receiptTx} onClose={() => setReceiptTx(null)} />
      )}
      {walletDetail && (
        <WalletDetailModal
          currency={walletDetail.currency}
          balance={walletDetail.balance}
          onClose={() => setWalletDetail(null)}
        />
      )}
    </div>
  );
}
